/*
    The app, investor and portal each display statements + documents in a single list.
    This utility sorts -> outputs the display data necessary for the components
*/

import { minBy } from 'lodash';

import {
  BorrowerDocumentAccess as AppBorrowerAccess,
  DocumentCategory,
  DocumentMailStatusMethod,
  DocumentMailStatusRecipientParty,
  DocumentType,
  LoanBillSettingsType,
  LoanDocumentDeliveryStatus,
} from '@willow/graphql-iso/src/app';
import { DocumentType as PortalDocumentType } from '@willow/graphql-iso/src/portal';
import {
  AuthorizedContactId,
  BorrowerId,
  DocumentId,
  EntityId,
  EscrowCompanyId,
  LoanEmailId,
  PaymentDueId,
  SideEffectId,
} from '@willow/types-iso';

import { DATE_FORMAT_SHORT_FULL_YEAR, dateFormat } from './dateFormat';

export const RevisedCallout = 'Replaced by revised copy';

export type DocumentMailStatusRecipient = {
  name: string;
  party?: DocumentMailStatusRecipientParty;
  borrowerId?: BorrowerId;
  authorizedContactId?: AuthorizedContactId;
  entityId?: EntityId;
  escrowCompanyId?: EscrowCompanyId;
};

export type DocumentMailStatus = {
  method: DocumentMailStatusMethod;
  date: string;
  testMode?: boolean;
  recipient?: DocumentMailStatusRecipient;
};

type DocumentMailStatusHistoryEntry = {
  method: DocumentMailStatusMethod;
  date: string | Date;
  testMode?: boolean | null;
  recipient?: {
    name: string;
    party?: DocumentMailStatusRecipientParty | null;
    borrowerId?: BorrowerId | null;
    authorizedContactId?: AuthorizedContactId | null;
    entityId?: EntityId | null;
    escrowCompanyId?: EscrowCompanyId | null;
  } | null;
};

// Reports the first action taken (e.g. a document tasked to mail and later mailed by working that
// task stays "tasked"), so re-mails and re-tasks don't move the status or the date
export const getDocumentMailStatus = (
  history: DocumentMailStatusHistoryEntry[] | undefined | null,
): DocumentMailStatus | undefined => {
  const original = minBy(history, (entry) => new Date(entry.date).getTime());
  if (!original) return undefined;

  const { recipient } = original;
  return {
    method: original.method,
    date: dateFormat(original.date, DATE_FORMAT_SHORT_FULL_YEAR),
    testMode: original.testMode ?? undefined,
    recipient: recipient
      ? {
          name: recipient.name,
          party: recipient.party ?? undefined,
          borrowerId: recipient.borrowerId ?? undefined,
          authorizedContactId: recipient.authorizedContactId ?? undefined,
          entityId: recipient.entityId ?? undefined,
          escrowCompanyId: recipient.escrowCompanyId ?? undefined,
        }
      : undefined,
  };
};

/* DEFINE TYPES FOR TABLE DISPLAY FORMATTING */
type BaseItem = {
  notificationType: 'document' | 'email' | 'manuallyUploaded';
  category?: DocumentCategory; // optional: the portal does not display categories
  title: string;
  subtitle?: string;
  callouts: string[];
  sortByDate: Date;
  createdAt: Date;
  version: number;
  date: Date | string; // date pre-sorted -> string formatted post-sorting
  borrowerAccess?: AppBorrowerAccess;
  isArchived: boolean;
  mailStatus?: DocumentMailStatus;
};

export type StatementItem = BaseItem & {
  id: DocumentId;
  paymentDueId: PaymentDueId;
  documentType: 'Statement';
  statementDate: Date;
  billSettingType?: LoanBillSettingsType;
};

export type DocumentItem = BaseItem & {
  id: DocumentId;
  documentType: 'Document';
  // The app and portal each generate their own DocumentType enum, and TS enums don't unify
  documentSpecificationType: DocumentType | PortalDocumentType;
  delivery?: { status: LoanDocumentDeliveryStatus; date?: string };
  externalRecipientName?: string;
};

export type EmailRecipientItem = {
  key: string;
  name: string;
  email?: string;
  emailSent: boolean;
  sentDate: string;
};

// One row per outbound email, grouped from the per-recipient copies that share a sideEffectId
export type EmailItem = BaseItem & {
  id: SideEffectId;
  documentType: 'Email';
  emailTemplate: string;
  // The primary borrower's copy when they received one, otherwise the earliest recipient's copy
  downloadLoanEmailId: LoanEmailId;
  recipients: EmailRecipientItem[];
};

export type DocumentDisplayItem = StatementItem | DocumentItem | EmailItem;

export const sortDocumentsByDate = (documents: DocumentDisplayItem[]): DocumentDisplayItem[] => {
  // Newest first, then newest version. Items alike on both must compare equal so the sort is stable
  // and leaves them in the order the server sent; returning a non-zero value either way reverses the
  // group on every sort and moves rows between pages of the table.
  const sortedByDate = documents.sort((a, b) => {
    const dateA = new Date(a.sortByDate).getTime();
    const dateB = new Date(b.sortByDate).getTime();

    return dateA === dateB ? b.version - a.version : dateB - dateA;
  });

  return sortedByDate;
};
